import React, { useEffect, useState } from "react";
import axios from "axios";
import DataTable, { TableColumn } from "react-data-table-component";
import * as XLSX from "sheetjs-style";
import "jspdf-autotable";
import { toast } from "react-toastify";
const FaFileExcel = require("react-icons/fa").FaFileExcel;

interface MeetingData {
    state_name: string;
    state_code: string | number;
    total_panchayats: number;
    panchayats_with_mom_generated: number;
    total_meetings_uploaded: number;
    final_submitted_mom: number;
    draft_mom: number;
}

const StateDashboard: React.FC<{ finYear: string; meetingType: any }> = ({
    finYear,
    meetingType,
}) => {
    const baseUrl = process.env.REACT_APP_BASE_URL;
    const [tableData, setTableData] = useState<MeetingData[]>([]);
    const [filterText, setFilterText] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    useEffect(() => {
        const fetchMeetings = async () => {
            setTableData([]);
            try {
                const response = await axios.get(`${baseUrl}api/state_report`, {
                    params: {
                        fin_year: finYear,
                        meeting_type: meetingType?.value !== "0" ? meetingType?.value : undefined,
                    },
                });
                if (response?.data?.msg_code === 400) {
                    toast.error(response?.data?.msg || "Something went wrong");
                    setTableData([]);
                    return;
                }
                setTableData(response?.data?.data || []);
            } catch (error) {
                console.error("Error fetching meeting data:", error);
            }
        };

        fetchMeetings();
    }, [finYear, meetingType, baseUrl]);

    const columns: TableColumn<MeetingData>[] = [
        {
            name: "S.No.",
            cell: (row, index) => (row as any).isTotal ? "" : (index + 1 + (currentPage - 1) * rowsPerPage).toString(),
            width: "80px",
        },
        {
            name: "State Name",
            selector: (row) => row.state_name,
            sortable: true,
            wrap: true,
            style: { whiteSpace: "normal" },
        },
        { name: "Total Panchayats", selector: (row) => row.total_panchayats, sortable: true },
        { name: "Panchayats with MoM Generated", selector: (row) => row.panchayats_with_mom_generated, sortable: true },
        { name: "Draft MoM", selector: (row) => row.draft_mom, sortable: true },
        { name: "Final Submitted", selector: (row) => row.final_submitted_mom, sortable: true },
        { name: "Total Meetings Uploaded", selector: (row) => row.total_meetings_uploaded, sortable: true },
    ];

    const filteredData = tableData.filter((item) =>
        (item.state_name ? String(item.state_name).toLowerCase() : "").includes(filterText.toLowerCase()) ||
        (item.state_code ? String(item.state_code).toLowerCase() : "").includes(filterText.toLowerCase())
    );

    const totalPanchayats = filteredData.reduce((sum, r) => sum + r.total_panchayats, 0);
    const totalMom = filteredData.reduce((sum, r) => sum + r.panchayats_with_mom_generated, 0);
    const totalMeetings = filteredData.reduce((sum, r) => sum + r.total_meetings_uploaded, 0);
    const totalDraft = filteredData.reduce((sum, r) => sum + r.draft_mom, 0);
    const totalFinal = filteredData.reduce((sum, r) => sum + r.final_submitted_mom, 0);

    const exportExcel = () => {
        const header = ["S.No.", "State Name","Total Panchayats", "MoM Generated",  "Draft MoM", "Final Submitted", "Total Meetings Uploaded",];
        const data = filteredData.map((row, index) => [
            index + 1,
            row.state_name,
            row.total_panchayats,
            row.panchayats_with_mom_generated,
            row.total_meetings_uploaded,
            row.draft_mom,
            row.final_submitted_mom,
        ]);

        const totalsRow = ["", "Total", totalMom, totalMeetings, totalDraft, totalFinal, totalPanchayats];
        const finalData = [header, ...data, totalsRow];

        const ws = XLSX.utils.aoa_to_sheet(finalData);
        ws["!cols"] = [{ wch: 8 }, { wch: 30 }, { wch: 20 }, { wch: 20 }, { wch: 15 }, { wch: 15 }, { wch: 20 }];

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "State Report");
        XLSX.writeFile(wb, "state_report.xlsx");
    };

    const totalRow: any = {
        state_name: "Total",
        panchayats_with_mom_generated: totalMom,
        total_panchayats: totalPanchayats,
        total_meetings_uploaded: totalMeetings,
        draft_mom: totalDraft,
        final_submitted_mom: totalFinal,
        isTotal: true,
    };

    return (
        <div className="mt-3">
            <div className="d-flex justify-content-between mb-2">
                <input
                    type="text"
                    className="form-control w-25"
                    placeholder="Search by state or code..."
                    value={filterText}
                    onChange={(e) => setFilterText(e.target.value)}
                />
                <button className="shiny-btn me-2" onClick={exportExcel}>
                    <FaFileExcel /> Export Excel
                </button>
            </div>

            <DataTable
                columns={columns}
                data={filteredData.length ? [...filteredData, totalRow] : []}
                conditionalRowStyles={[{ when: (row: any) => row.isTotal, style: { fontWeight: "bold", backgroundColor: "#f8f9fa", borderTop: "2px solid #dee2e6" } }]}
                highlightOnHover
                striped
            />
        </div>
    );
};

export default StateDashboard;